import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';

export interface Transform {
  x: number; // screen offset of world origin
  y: number;
  k: number; // scale
}

export interface PanZoomOptions {
  minK?: number;
  maxK?: number;
  /** Return true if the pointer-down at this world point grabs an item (then pans are suppressed). */
  onItemDown?: (wx: number, wy: number) => boolean;
  onItemDrag?: (wx: number, wy: number) => void;
  /** moved = false means it was a tap on the item. */
  onItemUp?: (moved: boolean) => void;
  /** Tap on empty space (pointer-down + up without moving). */
  onBackgroundTap?: (wx: number, wy: number) => void;
}

const DRAG_THRESHOLD = 4;

/**
 * Pan (drag, trackpad scroll), zoom (ctrl/⌘+wheel, pinch, mouse wheel), and smooth animated
 * moves for a full-size element. Pointer capture starts only once a drag passes a small
 * threshold, so plain clicks still reach child elements (SVG cards).
 */
export function usePanZoom(ref: RefObject<HTMLElement>, opts: PanZoomOptions = {}) {
  const [t, setT] = useState<Transform>({ x: 0, y: 0, k: 1 });
  const tRef = useRef(t);
  tRef.current = t;
  const optsRef = useRef(opts);
  optsRef.current = opts;
  const anim = useRef<number | null>(null);
  const minK = opts.minK ?? 0.08;
  const maxK = opts.maxK ?? 2.5;

  const clampK = useCallback((k: number) => Math.min(maxK, Math.max(minK, k)), [minK, maxK]);

  const set = useCallback((next: Transform) => {
    if (anim.current) cancelAnimationFrame(anim.current);
    anim.current = null;
    setT(next);
  }, []);

  const animateTo = useCallback((target: Transform, ms = 350) => {
    if (anim.current) cancelAnimationFrame(anim.current);
    const from = tRef.current;
    const start = performance.now();
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduce || ms <= 0) {
      setT(target);
      return;
    }
    const step = (now: number) => {
      const p = Math.min(1, (now - start) / ms);
      const e = 1 - Math.pow(1 - p, 3);
      setT({ x: from.x + (target.x - from.x) * e, y: from.y + (target.y - from.y) * e, k: from.k + (target.k - from.k) * e });
      anim.current = p < 1 ? requestAnimationFrame(step) : null;
    };
    anim.current = requestAnimationFrame(step);
  }, []);

  const zoomAt = useCallback(
    (sx: number, sy: number, factor: number, animate = false) => {
      const cur = tRef.current;
      const k = clampK(cur.k * factor);
      const wx = (sx - cur.x) / cur.k;
      const wy = (sy - cur.y) / cur.k;
      const next = { k, x: sx - wx * k, y: sy - wy * k };
      if (animate) animateTo(next, 200);
      else set(next);
    },
    [animateTo, clampK, set],
  );

  /** Center the given world point on screen, optionally at a new scale. */
  const centerOn = useCallback(
    (wx: number, wy: number, k?: number, animate = true) => {
      const el = ref.current;
      if (!el) return;
      const kk = clampK(k ?? tRef.current.k);
      const next = { k: kk, x: el.clientWidth / 2 - wx * kk, y: el.clientHeight / 2 - wy * kk };
      if (animate) animateTo(next);
      else set(next);
    },
    [animateTo, clampK, ref, set],
  );

  const fit = useCallback(
    (b: { minX: number; maxX: number; minY: number; maxY: number }, pad = 40, animate = true) => {
      const el = ref.current;
      if (!el || !isFinite(b.minX)) return;
      const w = Math.max(1, b.maxX - b.minX);
      const h = Math.max(1, b.maxY - b.minY);
      const k = clampK(Math.min((el.clientWidth - pad * 2) / w, (el.clientHeight - pad * 2) / h, 1.2));
      centerOn((b.minX + b.maxX) / 2, (b.minY + b.maxY) / 2, k, animate);
    },
    [centerOn, clampK, ref],
  );

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const toLocal = (e: { clientX: number; clientY: number }) => {
      const r = el.getBoundingClientRect();
      return { sx: e.clientX - r.left, sy: e.clientY - r.top };
    };
    const toWorld = (sx: number, sy: number) => {
      const c = tRef.current;
      return { wx: (sx - c.x) / c.k, wy: (sy - c.y) / c.k };
    };

    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const { sx, sy } = toLocal(e);
      // Pinch gestures and ctrl/⌘+wheel zoom. A classic mouse wheel (big integer steps,
      // no horizontal component) also zooms; trackpad two-finger scrolling pans.
      const mouseWheel = e.deltaMode !== 0 || (e.deltaX === 0 && Math.abs(e.deltaY) >= 40 && Number.isInteger(e.deltaY));
      if (e.ctrlKey || e.metaKey || mouseWheel) {
        const d = e.deltaMode === 1 ? e.deltaY * 16 : e.deltaY;
        zoomAt(sx, sy, Math.exp(-d * (e.ctrlKey ? 0.01 : 0.0015)));
      } else {
        const c = tRef.current;
        set({ ...c, x: c.x - e.deltaX, y: c.y - e.deltaY });
      }
    };

    const pointers = new Map<number, { sx: number; sy: number }>();
    let mode: 'none' | 'maybe' | 'pan' | 'item' | 'pinch' = 'none';
    let startS = { sx: 0, sy: 0 };
    let startT = tRef.current;
    let pinchStart = { dist: 1, k: 1, cx: 0, cy: 0, t: tRef.current };
    let itemMoved = false;

    const onDown = (e: PointerEvent) => {
      if (e.button !== 0 && e.pointerType === 'mouse') return;
      // Let buttons inside the canvas (e.g. "show more" controls) behave normally.
      if ((e.target as Element).closest?.('button, a, input, select, textarea, [data-no-pan]')) return;
      const p = toLocal(e);
      pointers.set(e.pointerId, p);
      if (pointers.size === 2) {
        const [a, b] = [...pointers.values()];
        pinchStart = { dist: Math.hypot(a.sx - b.sx, a.sy - b.sy) || 1, k: tRef.current.k, cx: (a.sx + b.sx) / 2, cy: (a.sy + b.sy) / 2, t: tRef.current };
        mode = 'pinch';
        el.setPointerCapture(e.pointerId);
        return;
      }
      startS = p;
      startT = tRef.current;
      const { wx, wy } = toWorld(p.sx, p.sy);
      if (optsRef.current.onItemDown?.(wx, wy)) {
        mode = 'item';
        itemMoved = false;
      } else mode = 'maybe';
    };
    const onMove = (e: PointerEvent) => {
      if (!pointers.has(e.pointerId)) return;
      const p = toLocal(e);
      pointers.set(e.pointerId, p);
      if (mode === 'pinch' && pointers.size === 2) {
        const [a, b] = [...pointers.values()];
        const dist = Math.hypot(a.sx - b.sx, a.sy - b.sy);
        const k = clampK(pinchStart.k * (dist / pinchStart.dist));
        const cx = (a.sx + b.sx) / 2;
        const cy = (a.sy + b.sy) / 2;
        const wx = (pinchStart.cx - pinchStart.t.x) / pinchStart.t.k;
        const wy = (pinchStart.cy - pinchStart.t.y) / pinchStart.t.k;
        set({ k, x: cx - wx * k, y: cy - wy * k });
        return;
      }
      if (mode === 'item') {
        if (!itemMoved) {
          if (Math.hypot(p.sx - startS.sx, p.sy - startS.sy) <= DRAG_THRESHOLD) return;
          // Capture only once it's a drag, so clicks and double-clicks still reach the item.
          itemMoved = true;
          el.setPointerCapture(e.pointerId);
        }
        const { wx, wy } = toWorld(p.sx, p.sy);
        optsRef.current.onItemDrag?.(wx, wy);
        return;
      }
      if (mode === 'maybe' && Math.hypot(p.sx - startS.sx, p.sy - startS.sy) > DRAG_THRESHOLD) {
        mode = 'pan';
        el.setPointerCapture(e.pointerId);
        el.classList.add('grabbing');
      }
      if (mode === 'pan') set({ ...startT, x: startT.x + (p.sx - startS.sx), y: startT.y + (p.sy - startS.sy) });
    };
    const onUp = (e: PointerEvent) => {
      if (!pointers.has(e.pointerId)) return;
      const p = pointers.get(e.pointerId)!;
      pointers.delete(e.pointerId);
      if (mode === 'item') optsRef.current.onItemUp?.(itemMoved);
      if (mode === 'maybe' && !(e.target as Element).closest?.('[data-node]')) {
        const { wx, wy } = toWorld(p.sx, p.sy);
        optsRef.current.onBackgroundTap?.(wx, wy);
      }
      if (pointers.size === 0) {
        mode = 'none';
        el.classList.remove('grabbing');
      } else if (mode === 'pinch') {
        // One finger lifted: continue as a pan from here.
        const [rest] = [...pointers.values()];
        startS = rest;
        startT = tRef.current;
        mode = 'pan';
      }
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    el.addEventListener('pointerdown', onDown);
    el.addEventListener('pointermove', onMove);
    el.addEventListener('pointerup', onUp);
    el.addEventListener('pointercancel', onUp);
    return () => {
      el.removeEventListener('wheel', onWheel);
      el.removeEventListener('pointerdown', onDown);
      el.removeEventListener('pointermove', onMove);
      el.removeEventListener('pointerup', onUp);
      el.removeEventListener('pointercancel', onUp);
    };
  }, [ref, zoomAt, set, clampK]);

  useEffect(() => () => {
    if (anim.current) cancelAnimationFrame(anim.current);
  }, []);

  return { t, set, animateTo, zoomAt, centerOn, fit };
}

/**
 * Whether a chart has room for its overlays (legend, minimap). On a small chart, such as a
 * phone either way up, they'd cover too much of it.
 */
export const roomForOverlays = (size: { w: number; h: number }) => size.w >= 640 && size.h >= 440;
